import * as React from "react";
import { useTranslation } from "react-i18next";
import { Field } from "./Field";
import { Avatar } from "./Avatar";
import { Button } from "./Button";

const MAX_BYTES = 2 * 1024 * 1024; // mirrors backend AVATAR_MAX_BYTES
const ACCEPT = ["image/png", "image/jpeg", "image/webp"];

type Props = {
  label: string;
  value: File | null;
  onChange: (file: File | null) => void;
  requirement?: "required" | "optional";
  hint?: string;
  error?: string;
};

/**
 * Optional avatar picker: a circular preview plus an outline button. Client-side
 * type/size guards mirror the backend so the upload fails fast before the POST.
 */
export function AvatarUploadField({
  label,
  value,
  onChange,
  requirement,
  hint,
  error,
}: Props) {
  const { t } = useTranslation("ui");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = React.useState<string>();
  const [preview, setPreview] = React.useState<string>();

  React.useEffect(() => {
    if (!value) {
      setPreview(undefined);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const accept = (file: File | undefined) => {
    if (!file) return;
    if (!ACCEPT.includes(file.type)) {
      setLocalError(t("photoTypeInvalid"));
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError(t("photoTooLarge"));
      return;
    }
    setLocalError(undefined);
    onChange(file);
  };

  const remove = () => {
    setLocalError(undefined);
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <Field
      label={label}
      requirement={requirement}
      hint={hint}
      error={error ?? localError}
    >
      <div className="flex items-center gap-4">
        {value && preview ? (
          <Avatar src={preview} alt={t("yourPhoto")} size="md" />
        ) : (
          <span
            aria-hidden="true"
            className="flex h-[52px] w-[52px] flex-none items-center justify-center rounded-full border border-dashed border-primary/30 text-h3 leading-none text-primary/30"
          >
            +
          </span>
        )}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            surface="light"
            as="button"
            type="button"
            onClick={() => inputRef.current?.click()}
          >
            {value ? t("replace") : t("addPhoto")}
          </Button>
          {value ? (
            <button
              type="button"
              onClick={remove}
              className="text-xs font-medium uppercase tracking-button text-primary/60 transition-colors hover:text-primary"
            >
              {t("remove")}
            </button>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT.join(",")}
          className="hidden"
          onChange={(e) => accept(e.target.files?.[0])}
        />
      </div>
    </Field>
  );
}
